import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import {
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { makeStyles } from 'tss-react/mui';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

import { thousands } from '../../../../utils/utils';
import { IProduct } from '../../../../interfaces/product';
import { SHeadTableCell } from '../../../../components/styledComponents/Tables';
import { IUser } from '../../../../interfaces/user';
import StatusIndicator from '../../../../components/styledComponents/StatusIndicator';
import homeStyles from '../../homeStyles';
import { getProductImage } from '../utils/getProductImage';
import { toAbsoluteUrl } from '../../../../../_base';
import { useDefineUserRole } from '../../../../hooks';
// import { useCreateChat } from '../../chats/chatsPage/hooks/useCreateChat';

interface IProductsProps {
  data: IProduct[];
  isAuthorized: boolean;
  isAdmin: boolean;
  me: IUser | undefined;
  addProductLoading: boolean;
  handleCartDialog: any;
  editAction?: any;
  handleDeleteDialog?: any;
}
const ProductsTableListForAdminComponent: React.FC<IProductsProps> = ({
  data,
  isAuthorized,
  isAdmin,
  me,
  addProductLoading,
  handleCartDialog,
  editAction,
  handleDeleteDialog,
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const homeClasses = homeStyles();
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR', 'ROLE_VENDOR_STAFF']);
  // const { createChatFetch } = useCreateChat();

  // const newChat = useCallback((userId?: string | number) => {
  //   createChatFetch({ userId });
  // }, []);

  const getMinPriceFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.min_price || '-'));
    }

    const minPrices = product.prices_values
      .map(pv => {
        if (pv.prices && pv.prices.length > 0) {
          return Math.min(...pv.prices.map(p => p.price));
        }
        return pv.vendor?.min_price || null;
      })
      .filter((price): price is number => price !== null);

    if (minPrices.length === 0) {
      return thousands(String(product.min_price || '-'));
    }

    return thousands(String(Math.min(...minPrices)));
  }, []);

  const getTotalStockFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.stock_quantity || '-'));
    }

    const totalStock = product.prices_values.reduce((sum, pv) => {
      return sum + (pv.vendor?.stock_quantity || 0);
    }, 0);

    return totalStock > 0 ? thousands(String(totalStock)) : '-';
  }, []);

  const getTotalOrderFromOffers = useCallback((product: IProduct): string => {
    if (!product.prices_values || product.prices_values.length === 0) {
      return thousands(String(product.order_quantity || '-'));
    }

    const totalOrder = product.prices_values.reduce((sum, pv) => {
      return sum + (pv.vendor?.order_quantity || 0);
    }, 0);

    return totalOrder > 0 ? thousands(String(totalOrder)) : '-';
  }, []);

  return (
    <Row>
      <Col>
        {!data || !data.length ? (
          <Typography className={classes.empty} component='h5' variant='h5'>
            {intl.formatMessage({ id: 'COMMON.LIST.EMPTY' })}
          </Typography>
        ) : (
          <>
            <Card className={classes.card}>
              <CardContent className={homeClasses.classes.tableContainer}>
                <Table aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {isAuthorized && isAdmin && (
                        <SHeadTableCell className={classes.cell}>
                          {intl.formatMessage({ id: 'PRODUCT.TABLE.ID' })}
                        </SHeadTableCell>
                      )}
                      <SHeadTableCell className={classes.cell}>
                        {intl.formatMessage({ id: 'PRODUCT.TABLE.PREVIEW' })}
                      </SHeadTableCell>
                      <SHeadTableCell className={classes.cell}>
                        {intl.formatMessage({ id: 'PRODUCT.TABLE.NAME' })}
                      </SHeadTableCell>
                      <SHeadTableCell className={classes.cell}>
                        {intl.formatMessage({ id: 'PRODUCT.TABLE.CATEGORY.NAME' })}
                      </SHeadTableCell>
                      <SHeadTableCell className={classes.cell}>
                        {intl.formatMessage({ id: 'PRODUCT.MIN.PRICE' })}
                      </SHeadTableCell>
                      <SHeadTableCell className={`${classes.cell} ${classes.cellNarrow}`}>
                        Доступно<br />со склада
                      </SHeadTableCell>
                      <SHeadTableCell className={`${classes.cell} ${classes.cellNarrow}`}>
                        Доступно<br />под заказ
                      </SHeadTableCell>
                      {!isBuyer && isAuthorized && (
                        <SHeadTableCell className={`${classes.cell} ${classes.cellNarrow}`}>
                          Акт.
                        </SHeadTableCell>
                      )}
                      <SHeadTableCell className={classes.cell}>
                        {intl.formatMessage({ id: 'PRODUCT.TABLE.ACTIONS' })}
                      </SHeadTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map(item => (
                      <TableRow key={item.id}>
                        {isAuthorized && isAdmin && (
                          <TableCell scope='row' className={classes.rowId}>
                            {item.id || '-'}
                          </TableCell>
                        )}
                        <TableCell className={classes.rowElements}>
                          <RouterLink to={`/products/edit/${item.id}`}>
                            <img
                              src={getProductImage(item)}
                              className={classes.img}
                              alt=''
                              onError={(e: any) => {
                                e.target.src = toAbsoluteUrl('/images/placeholder.png');
                              }}
                            />
                          </RouterLink>
                        </TableCell>
                        <TableCell className={classes.rowNameWide}>
                          <RouterLink to={`/products/edit/${item.id}`}>
                            {item.name || '-'}
                          </RouterLink>
                        </TableCell>
                        <TableCell className={classes.rowElements}>
                          {item.category?.name || '-'}
                        </TableCell>
                        <TableCell className={classes.rowElements}>
                          {getMinPriceFromOffers(item)}
                        </TableCell>
                        <TableCell className={classes.rowElements}>
                          {getTotalStockFromOffers(item)}
                        </TableCell>
                        <TableCell className={classes.rowElements}>
                          {getTotalOrderFromOffers(item)}
                        </TableCell>
                        {!isBuyer && isAuthorized && (
                          <TableCell className={classes.rowElements}>
                            <StatusIndicator isActive={item.active} />
                          </TableCell>
                        )}
                        <TableCell className={classes.rowElements}>
                          <div>
                            {!isVendor && !isAdmin && (
                              <IconButton
                                disabled={addProductLoading}
                                onClick={() => handleCartDialog(item)}
                              >
                                <AddShoppingCartIcon color='primary' />
                              </IconButton>
                            )}

                            {!isBuyer && (
                              <div className={classes.actions}>
                                {editAction && (
                                  <>
                                    <IconButton onClick={() => editAction(item)}>
                                      <EditIcon color='primary' />
                                    </IconButton>
                                  </>
                                )}
                                {isAdmin && handleDeleteDialog && (
                                  <IconButton
                                    onClick={() => {
                                      handleDeleteDialog(item);
                                    }}
                                  >
                                    <DeleteIcon color='error' />
                                  </IconButton>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </Col>
    </Row>
  );
};

const useStyles = makeStyles()(theme => {
  return {
    empty: {
      marginBottom: 20,
    },
    card: {
      marginBottom: theme.spacing(3),
      boxShadow: 'none !important',
    },
    img: {
      maxHeight: 55,
    },
    cell: {
      fontWeight: 'bold',
      fontSize: 14,
      whiteSpace: 'normal' as const,
    },
    cellNarrow: {
      width: 90,
    },
    rowElements: {
      fontSize: '14px !important',
    },
    rowNameWide: {
      fontSize: '14px !important',
      maxWidth: 280,
      wordBreak: 'break-word' as const,
    },
    rowId: {
      fontSize: '12px !important',
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
});

export default ProductsTableListForAdminComponent;
